import isEmpty from 'lodash-es/isEmpty';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import SockJS from 'sockjs-client';
import { over } from 'stompjs';
import { getEmailStatistics, getInboxPaginated, getSMTPConfig, getUnreadCount } from 'wisestep-api';

import { useNewEmailStore } from '../../features/emailSettings/components/twoWayEmailSync/Util/NewEmailStore';
import { getAuthToken } from '../../utils/token';

// import NotificationLayout from './components/NotificationLayout';
import NotificationDTO, { DataType } from './types/NotificationDTO';
import { useNotificationSSEStore } from './utils/NotificationSSEStore';

export interface uiObjectType {
  notificationTitle: string;
  notificationSubtext: string;
  notificationType: string;
  link: string;
}
export interface stateObject {
  title: string;
  type: string;
  link: string;
}
const NotificationSSE = () => {
  const { enqueueSnackbar } = useSnackbar();
  const setNotificationSSE = useNotificationSSEStore((state) => state.setNotificationSSE);
  const setInBoxData = useNewEmailStore((state) => state.setInBoxData);
  const setConfiguredAccounts = useNewEmailStore((state) => state.setConfiguredImapAccounts);
  const setUnreadCount = useNewEmailStore((state) => state.setUnreadCount);
  const configuredImapAccounts = useNewEmailStore((state) => state.configuredImapAccounts);
  const [reConnect, setReConnect] = useState(false);

  const REACT_APP_API_ENDPOINT_SOCKET = process.env.REACT_APP_API_ENDPOINT_SOCKET as string;
  //const [uiObject, setUiObject] = useState<uiObjectType>();
  const authCookie = getAuthToken();
  const { closeSnackbar } = useSnackbar();
  const handleDismiss = useCallback(() => {
    closeSnackbar();
  }, [closeSnackbar]);

  useEffect(() => {
    const getCount = async () => {
      const response = await getUnreadCount();
      if (response.data) setNotificationSSE(response.data.count);
    };
    getCount();
  }, [setNotificationSSE]);

  const LoadEmails = useCallback(async () => {
    const loadDataFirstTime = await getInboxPaginated(
      configuredImapAccounts[0].recruiterEmail,
      0,
      ''
    );
    if (loadDataFirstTime.data) {
      setInBoxData(loadDataFirstTime.data);
      const inboxStatsResponse = await getEmailStatistics(configuredImapAccounts[0].recruiterEmail);
      if (inboxStatsResponse.data) setUnreadCount(inboxStatsResponse.data.unread);
    }
  }, [configuredImapAccounts, setInBoxData, setUnreadCount]);

  useEffect(() => {
    console.log('executing socket connection');
    const socket = SockJS(`${REACT_APP_API_ENDPOINT_SOCKET}/ws?Authorization=${authCookie}`);
    const stompClient = over(socket);
    console.log('socket ', socket);
    console.log('stompClient ', stompClient);
    stompClient.connect(
      'guest',
      'guest',
      () => {
        console.log('connected successfully');
        stompClient.subscribe(
          '/user/queue/notifications',
          (event) => {
            console.log('printing event ', event);
            const responseObject: NotificationDTO = JSON.parse(event.body);

            if (!isEmpty(responseObject.workspaceId)) {
              if (responseObject?.status === 'NEW') {
                setNotificationSSE(responseObject.totalUnread);
              } else {
                setNotificationSSE(responseObject?.totalUnread - 1);
              }

              if (responseObject?.type === 'EMAIL_DATA') {
                LoadEmails();
              }

              if (responseObject?.type != 'EMAIL_DATA') {
                const tempStore: stateObject = NotificationHandler(
                  responseObject.workspaceName,
                  getValue(responseObject?.data, 'jobId') ?? '',
                  getValue(responseObject?.data, 'id'),
                  getValue(responseObject?.data, 'assignmentId') ?? '',
                  responseObject.type,
                  getValue(responseObject?.data, 'harvestName') ?? ''
                ) || { title: '', type: '', link: '' };

                enqueueSnackbar(tempStore.title, {
                  variant: 'customNotification',
                  notificationTitle: tempStore.title,
                  notificationSubtext: getValue(responseObject?.data, 'title'),
                  notificationType: tempStore.type,
                  onClickLink: tempStore.link,
                  anchorOrigin: { horizontal: 'right', vertical: 'top' },
                  autoHideDuration: 5000,
                  taskDetails: responseObject,
                });
              }
            }
          },
          {
            'x-expires': 180000,
          }
        );
      },
      (err) => {
        console.log(err);
        if (err.headers?.message === 'Connection to broker closed.') {
          console.log('Connecting to broker');
          setReConnect(!reConnect);
        }
      }
    );
  }, [
    LoadEmails,
    REACT_APP_API_ENDPOINT_SOCKET,
    authCookie,
    enqueueSnackbar,
    handleDismiss,
    setNotificationSSE,
    reConnect,
  ]);
  useEffect(() => {
    const loadData = async () => {
      const configuredImapAccount = await getSMTPConfig();
      if (Array.isArray(configuredImapAccount.data)) {
        setConfiguredAccounts(configuredImapAccount.data);
        const loadDataFirstTime = await getInboxPaginated(
          configuredImapAccount.data[0].recruiterEmail,
          0,
          ''
        );
        if (loadDataFirstTime.data) {
          setInBoxData(loadDataFirstTime.data);
          const inboxStatsResponse = await getEmailStatistics(
            configuredImapAccount.data[0].recruiterEmail
          );
          if (inboxStatsResponse.data) setUnreadCount(inboxStatsResponse.data.unread);
        }
      }
    };
    loadData();
  }, [setConfiguredAccounts, setInBoxData, setUnreadCount]);
  const NotificationHandler = (
    workspaceName: string,
    jobId: string,
    _interactionId: string,
    _workAssignmentId: string,
    type: string,
    harvestName: string
  ) => {
    // const workspaceName: string = workspaceName;
    // const jobId: string = jobId;
    // const interactionId: string = interactionId;
    // const workAssignmentId: string = workAssignmentId;
    let stateHandlerObject: stateObject = {
      title: '',
      type: '',
      link: '',
    };

    const responseHandler = {
      ['CANDIDATE_APPLICATION']: () => {
        let applicationLink = '';

        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/2/pipeline`;

        stateHandlerObject = {
          title: 'New Application',
          type: type,
          link: applicationLink,
        };
      },
      ['CANDIDATE_SUBMISSION']: () => {
        const link = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
        stateHandlerObject = {
          title: 'New Submission',
          type: type,
          link: link,
        };
      },
      ['NOTE_ADDED']: () => {
        const link = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
        stateHandlerObject = {
          title: 'Updated Submission',
          type: type,
          link: link,
        };
      },
      ['STAGE_STATUS_CHANGED']: () => {
        const link = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
        stateHandlerObject = {
          title: 'Updated Submission',
          type: type,
          link: link,
        };
      },
      ['OVERDUE_TASK']: () => {
        const link = `https://${workspaceName}.wisestep.com/tasks`;
        stateHandlerObject = {
          title: 'OVERDUE TASK',
          type: type,
          link: link,
        };
      },
      ['CLOSE_JOB']: () => {
        const jobLink = `https://${workspaceName}.wisestep.com/jobcrm/${jobId}/false/0/pipeline`;

        stateHandlerObject = {
          title: 'Job Closed',
          type: type,
          link: jobLink,
        };
      },
      ['JOB_ASSIGNMENT']: () => {
        const link = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/0/pipeline`;
        stateHandlerObject = {
          title: 'Job Assigned',
          type: type,
          link: link,
        };
      },
      ['HARVESTING']: () => {
        stateHandlerObject = {
          title: `Harvesting Report For: ${harvestName}`,
          type: type,
          link: '',
        };
      },
      ['INBOUND_SMS']: () => {
        stateHandlerObject = {
          title: `New Inbound SMS`,
          type: type,
          link: `https://${workspaceName}.wisestep.com/sms-logs`,
        };
      },

      ['NEW_EMAIL']: () => {
        stateHandlerObject = {
          title: `You Have Received New Emails`,
          type: type,
          link: `https://${workspaceName}.wisestep.com/email-settings/logs/inbox/`,
        };
      },
      ['EMAIL_DATA']: () => {},
      ['RTR_RESPONSE']: () => {
        stateHandlerObject = {
          title: `RTR Response Received`,
          type: type,
          link: '',
        };
      },
      ['INFORMATION_RESPONSE']: () => {
        stateHandlerObject = {
          title: `Information Request Response Received`,
          type: type,
          link: '',
        };
      },
      ['EVALUATION_RESPONSE']: () => {
        stateHandlerObject = {
          title: `Evaluation Response Received`,
          type: type,
          link: '',
        };
      },
    };
    try {
      responseHandler[type]();
      return stateHandlerObject;
    } catch (e) {
      console.log('Exception while processing notification NOTIFICATION_ERROR', e);
    }
  };
  // const timerToReset = () => setTimeout(() => setBool(false), 30000);
  const getValue = (notification: DataType[], key: string) => {
    let value = '';
    notification.map((single) => {
      if (single.key === key) value = single.value;
    });
    return value;
  };
  // const stateHandler = (title: string, type: string, link: string, response) => {
  //   if (response) {
  //     console.log(response);
  //     setUiObject({
  //       notificationTitle: title,
  //       notificationSubtext: getValue(response?.data, 'title'),
  //       notificationType: type,
  //       link: link,
  //     });
  //   }
  // };

  return <></>;
};
export default NotificationSSE;
